import { useEffect, useRef, useCallback, useState } from 'react';

interface UseInfiniteScrollOptions {
    threshold?: number;
    rootMargin?: string;
    enabled?: boolean;
}

interface UseInfiniteScrollReturn {
    observerRef: React.RefObject<any>;
    isLoading: boolean;
}

export const useInfiniteScroll = (
    onLoadMore: () => void,
    options: UseInfiniteScrollOptions = {}
): UseInfiniteScrollReturn => {
    const {
        threshold = 0.1,
        rootMargin = '50px',
        enabled = true
    } = options;

    const observerRef = useRef<any>(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleObserver = useCallback(
        (entries: IntersectionObserverEntry[]) => {
            const [target] = entries;
            if (target.isIntersecting && enabled && !isLoading) {
                setIsLoading(true);
                onLoadMore();
                setTimeout(() => setIsLoading(false), 500);
            }
        },
        [enabled, onLoadMore, isLoading]
    );

    useEffect(() => {
        const element = observerRef.current;
        if (!element || !enabled) return;

        const observer = new IntersectionObserver(handleObserver, {
            threshold,
            rootMargin
        });

        observer.observe(element);

        return () => {
            if (element) {
                observer.unobserve(element);
            }
        };
    }, [handleObserver, threshold, rootMargin, enabled]);

    return { observerRef, isLoading };
};